import API_BASE_URL from "./config";
import React, {
  useState,
  useEffect,
  useRef
} from "react";

import "./App.css";

import FilePicker from "./FilePicker";

function App() {

  // =========================================
  // 🔥 AUTH
  // =========================================
  const [deviceData, setDeviceData] =
    useState(null);

  const [isLoggedIn, setIsLoggedIn] =
    useState(false);

  const [timeLeft, setTimeLeft] =
    useState(0);

  const pollRef = useRef(null);

  // =========================================
  // 🔥 CONFIG
  // =========================================
  const [source, setSource] =
    useState(null);

  const [destination, setDestination] =
    useState(null);

  const [savedConfig, setSavedConfig] =
    useState(null);

  // =========================================
  // 🔥 NAVIGATION
  // =========================================
  const [activeTab, setActiveTab] =
    useState("config");

  // =========================================
  // 🔥 CAMPAIGNS
  // =========================================
  const [campaigns, setCampaigns] =
    useState([]);

  const [editingCampaign, setEditingCampaign] =
    useState(null);

  // =========================================
  // 🔥 SYNC SETTINGS
  // =========================================
  const [campaignName, setCampaignName] =
    useState("");

  const [syncInterval, setSyncInterval] =
    useState(60);

  // =========================================
  // 🔥 WHATSAPP SETTINGS
  // =========================================
  const [whatsappEnabled, setWhatsappEnabled] =
    useState(false);

  const [templates, setTemplates] =
    useState([]);

  const [phoneColumn, setPhoneColumn] =
    useState("");

  const [selectedTemplate, setSelectedTemplate] =
    useState("");

  const [columns, setColumns] =
    useState([]);

  // =========================================
  // 🔥 AI FOLLOWUP
  // =========================================
  const [aiFollowupEnabled, setAiFollowupEnabled] =
    useState(false);

  const [followupDelay, setFollowupDelay] =
    useState(30);

  // =========================================
  // 🔥 INITIAL AUTH
  // =========================================
  useEffect(() => {

    const initialize = async () => {

      try {

        const authRes = await fetch(
          `${API_BASE_URL}/poll-token`,
          {
            credentials: "include"
          }
        );

        const authData =
          await authRes.json();

        if (
          authData.status ===
          "authenticated"
        ) {

          setIsLoggedIn(true);

          return;
        }

        const res = await fetch(
          `${API_BASE_URL}/device-login`,
          {
            credentials: "include"
          }
        );

        const data =
          await res.json();

        setDeviceData(data);

        setTimeLeft(
          data.expires_in
        );

      } catch (err) {

        console.error(err);
      }
    };

    initialize();

  }, []);

  // =========================================
  // 🔥 TIMER
  // =========================================
  useEffect(() => {

    if (!deviceData || isLoggedIn)
      return;

    const interval =
      setInterval(() => {

        setTimeLeft(prev =>
          prev > 0
            ? prev - 1
            : 0
        );

      }, 1000);

    return () =>
      clearInterval(interval);

  }, [deviceData, isLoggedIn]);

  // =========================================
  // 🔥 POLL LOGIN
  // =========================================
  useEffect(() => {

    if (!deviceData || isLoggedIn)
      return;

    const poll = async () => {

      try {

        const res = await fetch(
          `${API_BASE_URL}/poll-token`,
          {
            credentials: "include"
          }
        );

        const data =
          await res.json();

        if (
          data.status ===
          "authenticated"
        ) {

          setIsLoggedIn(true);

          clearInterval(
            pollRef.current
          );

          return;
        }

      } catch (err) {

        console.error(err);
      }
    };

    pollRef.current =
      setInterval(
        poll,
        5000
      );

    return () =>
      clearInterval(
        pollRef.current
      );

  }, [deviceData, isLoggedIn]);

  // =========================================
  // 🔥 LOAD CAMPAIGNS
  // =========================================
  const loadCampaigns =
    async () => {

      try {

        const res = await fetch(
          `${API_BASE_URL}/campaigns`
        );

        if (!res.ok) {

          setCampaigns([]);

          return;
        }

        const data =
          await res.json();

        if (
          Array.isArray(data)
        ) {

          setCampaigns(data);

        } else {

          setCampaigns([]);
        }

      } catch {

        setCampaigns([]);
      }
    };

  // =========================================
  // 🔥 AUTO REFRESH
  // =========================================
  useEffect(() => {

    if (!isLoggedIn)
      return;

    loadCampaigns();

    const interval =
      setInterval(() => {

        loadCampaigns();

      }, 5000);

    return () =>
      clearInterval(interval);

  }, [isLoggedIn]);

  // =========================================
  // 🔥 LOAD TEMPLATES
  // =========================================
  const loadTemplates =
    async () => {

      try {

        const res = await fetch(
          `${API_BASE_URL}/whatsapp/templates`
        );

        const data =
          await res.json();

        if (
          data.templates
        ) {

          setTemplates(
            data.templates
          );
        }

      } catch (err) {

        console.error(err);
      }
    };

  // =========================================
  // 🔥 LOGOUT
  // =========================================
  const handleLogout =
    async () => {

      try {

        await fetch(
          `${API_BASE_URL}/logout`,
          {
            method: "POST",
            credentials: "include"
          }
        );

        setIsLoggedIn(false);

        setDeviceData(null);

        window.location.reload();

      } catch (err) {

        console.error(err);
      }
    };

 // =========================================
// 🔥 COPY CODE
// =========================================
const copyCode = async () => {

  try {

    if (
      !deviceData?.user_code
    ) {
      return;
    }

    // =========================================
    // 🔥 MODERN CLIPBOARD
    // =========================================
    if (
      navigator.clipboard &&
      window.isSecureContext
    ) {

      await navigator.clipboard.writeText(
        deviceData.user_code
      );

      alert("Code copied ✅");

      return;
    }

    // =========================================
    // 🔥 FALLBACK COPY
    // =========================================
    const textArea =
      document.createElement("textarea");

    textArea.value =
      deviceData.user_code;

    textArea.style.position =
      "fixed";

    textArea.style.left =
      "-999999px";

    document.body.appendChild(
      textArea
    );

    textArea.focus();

    textArea.select();

    document.execCommand("copy");

    textArea.remove();

    alert("Code copied ✅");

  } catch (err) {

    console.error(
      "Copy failed:",
      err
    );

    alert(
      "Unable to copy code"
    );
  }
};

  // =========================================
  // 🔥 FORMAT TIMER
  // =========================================
  const formatTime = (
    seconds
  ) => {

    const min =
      Math.floor(
        seconds / 60
      );

    const sec =
      seconds % 60;

    return `${min}:${sec < 10 ? "0" : ""}${sec}`;
  };
  // =========================================
  // 🔥 START / UPDATE CAMPAIGN
  // =========================================
  const handleStartCampaign =
    async () => {

      try {

        const payload = {

          // =========================================
          // 🔥 BASIC
          // =========================================
          name:
            campaignName.trim() ||
            "Campaign",

          // =========================================
          // 🔥 SOURCE TYPE
          // =========================================
          source_type:
            savedConfig.source.source_type,

          // =========================================
          // 🔥 SOURCE ONEDRIVE
          // =========================================
          source_file_id:
            savedConfig.source.file_id || null,

          source_sheets:
            savedConfig.source.sheets || null,

          // =========================================
          // 🔥 SOURCE GOOGLE SHEETS
          // =========================================
          source_gsheet_url:
            savedConfig.source.gsheet_url || null,

          source_gsheet_name:
            savedConfig.source.gsheet_name || null,

          // =========================================
          // 🔥 DESTINATION TYPE
          // =========================================
          destination_type:
            savedConfig.destination.destination_type,

          // =========================================
          // 🔥 DESTINATION ONEDRIVE
          // =========================================
          destination_file_id:
            savedConfig.destination.file_id || null,

          destination_sheet:
            savedConfig.destination.sheet || null,

          // =========================================
          // 🔥 DESTINATION GOOGLE SHEETS
          // =========================================
          destination_gsheet_url:
            savedConfig.destination.gsheet_url || null,

          destination_gsheet_name:
            savedConfig.destination.gsheet_name || null,

          // =========================================
          // 🔥 SYNC
          // =========================================
          interval_seconds:
            Number(syncInterval),

          // =========================================
          // 🔥 WHATSAPP
          // =========================================
          whatsapp_enabled:
            whatsappEnabled,

          phone_column:
            phoneColumn,

          template_name:
            selectedTemplate,

          // =========================================
          // 🔥 AI FOLLOWUP
          // =========================================
          ai_followup_enabled:
            aiFollowupEnabled,

          followup_delay_minutes:
            Number(followupDelay)
        };

        // =========================================
        // 🔥 EDIT
        // =========================================
        if (
          editingCampaign
        ) {

          await fetch(
            `${API_BASE_URL}/campaign/${editingCampaign.id}`,
            {
              method: "PUT",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify(
                  payload
                ),
            }
          );

          alert(
            "Campaign updated ✅"
          );

          setEditingCampaign(null);

        } else {

          // =========================================
          // 🔥 CREATE
          // =========================================
          await fetch(
            `${API_BASE_URL}/create-campaign`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify(
                  payload
                ),
            }
          );

          alert(
            "Campaign created 🚀"
          );
        }

        loadCampaigns();

        setActiveTab(
          "dashboard"
        );

      } catch (err) {

        console.error(err);

        alert(
          "Something went wrong"
        );
      }
    };

  // =========================================
  // 🔥 LOGIN SCREEN
  // =========================================
  if (!isLoggedIn) {

    return (
      <div className="login-fullscreen">

        <div className="login-card">

          <h2>
            Lead Management Agent
          </h2>

          {deviceData?.user_code ? (

            <div className="login-box">

              <h3>
                Microsoft Login Required
              </h3>

              <p>

                Visit{" "}

                <a
                  href={
                    deviceData.verification_uri
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  microsoft.com/devicelogin
                </a>

              </p>

              <h1 className="device-code">

                {
                  deviceData.user_code
                }

              </h1>

              <button
                className="copy-btn"
                onClick={
                  copyCode
                }
              >
                Copy Code
              </button>

              <p className="timer">

                Code expires in{" "}

                {
                  formatTime(
                    timeLeft
                  )
                }

              </p>

            </div>

          ) : (

            <div className="login-box">

              <h3>
                Preparing Authentication...
              </h3>

            </div>
          )}

        </div>

      </div>
    );
  }

  // =========================================
  // 🔥 MAIN APP
  // =========================================
  return (

    <div className="app-container">

      {/* ========================================= */}
      {/* 🔥 SIDEBAR */}
      {/* ========================================= */}
      <div className="sidebar">

        <h3>
          Quick Actions
        </h3>

        <button
          className="sidebar-btn"
          onClick={() => {

            // 🔥 RESET EVERYTHING
            setEditingCampaign(null);

            setSavedConfig(null);

            setSource(null);

            setDestination(null);

            setCampaignName("");

            setSyncInterval(60);

            setWhatsappEnabled(false);

            setPhoneColumn("");

            setSelectedTemplate("");

            setAiFollowupEnabled(false);

            setFollowupDelay(30);

            setColumns([]);

            setActiveTab("config");
          }}
        >
          New Schedule
        </button>

        <button
          className="sidebar-btn"
          onClick={() =>
            setActiveTab(
              "dashboard"
            )
          }
        >
          Dashboard
        </button>

        <button
          className="sidebar-btn"
          onClick={
            handleLogout
          }
        >
          Logout
        </button>

      </div>

      {/* ========================================= */}
      {/* 🔥 MAIN CONTENT */}
      {/* ========================================= */}
      <div className="main">

        <div className="header">
          Lead Management Agent
        </div>
        {/* ========================================= */}
        {/* 🔥 FILE CONFIG */}
        {/* ========================================= */}
        {activeTab ===
          "config" && (

          <div className="card">

            <div className="section-title">
              File Configuration
            </div>

            <FilePicker

              onSourceSelect={
                setSource
              }

              onDestinationSelect={
                (data) => {

                  setDestination(data);

                  if (
                    data?.columns
                  ) {

                    setColumns(
                      data.columns
                    );
                  }
                }
              }

            />

            <button
              className="button"
              style={{
                marginTop:
                  "20px"
              }}
              onClick={() => {

                if (
                  !source ||
                  !destination
                ) {

                  alert(
                    "Please select source and destination"
                  );

                  return;
                }

                setSavedConfig({
                  source,
                  destination,
                });

                setCampaignName(
                  destination.sheet ||
                  destination.gsheet_name ||
                  "Campaign"
                );

                setActiveTab(
                  "sync"
                );
              }}
            >
              Next →
            </button>

          </div>
        )}

        {/* ========================================= */}
        {/* 🔥 SYNC SETTINGS */}
        {/* ========================================= */}
        {activeTab ===
          "sync" && (

          <div className="card">

            <div className="section-title">
              Sync Configuration
            </div>

            <label>
              Campaign Name
            </label>

            <input
              type="text"
              className="dropdown"
              value={
                campaignName
              }
              onChange={(e) =>
                setCampaignName(
                  e.target.value
                )
              }
            />

            <label
              style={{
                marginTop:
                  "15px",
                display:
                  "block"
              }}
            >
              Sync Interval
              (seconds)
            </label>

            <input
              type="number"
              className="dropdown"
              value={
                syncInterval
              }
              onChange={(e) =>
                setSyncInterval(
                  e.target.value
                )
              }
            />

            <button
              className="button"
              style={{
                marginTop:
                  "20px"
              }}
              onClick={() => {

                loadTemplates();

                setActiveTab(
                  "whatsapp"
                );
              }}
            >
              Next →
            </button>

          </div>
        )}

        {/* ========================================= */}
        {/* 🔥 WHATSAPP SETTINGS */}
        {/* ========================================= */}
        {activeTab ===
          "whatsapp" && (

          <div className="card">

            <div className="section-title">
              WhatsApp Settings
            </div>

            {/* ENABLE */}
            <label>
              Enable WhatsApp
            </label>

            <div
              style={{
                marginTop: "10px",
                marginBottom: "20px"
              }}
            >

              <input
                type="checkbox"
                checked={
                  whatsappEnabled
                }
                onChange={(e) =>
                  setWhatsappEnabled(
                    e.target.checked
                  )
                }
              />

            </div>

            {/* SETTINGS */}
            {whatsappEnabled && (
              <>

                {/* PHONE COLUMN */}
                <label>
                  Phone Number Column
                </label>

                <select
                  className="dropdown"
                  value={phoneColumn}
                  onChange={(e) =>
                    setPhoneColumn(
                      e.target.value
                    )
                  }
                >

                  <option value="">
                    Select Phone Column
                  </option>

                  {columns.map(
                    (
                      col,
                      index
                    ) => (

                      <option
                        key={index}
                        value={col}
                      >
                        {col}
                      </option>

                    )
                  )}

                </select>

                {/* TEMPLATE */}
                <label
                  style={{
                    marginTop:
                      "20px",
                    display:
                      "block"
                  }}
                >
                  WhatsApp Template
                </label>

                <select
                  className="dropdown"
                  value={
                    selectedTemplate
                  }
                  onChange={(e) =>
                    setSelectedTemplate(
                      e.target.value
                    )
                  }
                >

                  <option value="">
                    Select Template
                  </option>

                  {templates.map(
                    (
                      template,
                      index
                    ) => (

                      <option
                        key={index}
                        value={
                          template.name
                        }
                      >
                        {template.name}
                      </option>

                    )
                  )}

                </select>

                {/* AI FOLLOWUP */}
                <label
                  style={{
                    marginTop:
                      "20px",
                    display:
                      "block"
                  }}
                >
                  Enable AI Followup
                </label>

                <div
                  style={{
                    marginTop: "10px",
                    marginBottom: "20px"
                  }}
                >

                  <input
                    type="checkbox"
                    checked={
                      aiFollowupEnabled
                    }
                    onChange={(e) =>
                      setAiFollowupEnabled(
                        e.target.checked
                      )
                    }
                  />

                </div>

                {/* DELAY */}
                {aiFollowupEnabled && (
                  <>

                    <label>
                      Followup Delay
                      (minutes)
                    </label>

                    <input
                      type="number"
                      className="dropdown"
                      value={
                        followupDelay
                      }
                      onChange={(e) =>
                        setFollowupDelay(
                          e.target.value
                        )
                      }
                    />

                  </>
                )}

              </>
            )}

            <button
              className="button"
              style={{
                marginTop:
                  "20px"
              }}
              onClick={() =>
                setActiveTab(
                  "preview"
                )
              }
            >
              Next →
            </button>

          </div>
        )}

        {/* ========================================= */}
        {/* 🔥 PREVIEW */}
        {/* ========================================= */}
        {activeTab ===
          "preview" && (

          <div className="card">

            <div className="section-title">
              Campaign Preview
            </div>

            <p>
              <strong>
                Campaign:
              </strong>{" "}
              {campaignName}
            </p>

            <p>
              <strong>
                Source Type:
              </strong>{" "}
              {savedConfig?.source?.source_type}
            </p>

            <p>
              <strong>
                Destination Type:
              </strong>{" "}
              {savedConfig?.destination?.destination_type}
            </p>

            <p>
              <strong>
                Sync Interval:
              </strong>{" "}
              {syncInterval} seconds
            </p>

            <p>
              <strong>
                WhatsApp:
              </strong>{" "}
              {whatsappEnabled
                ? "Enabled"
                : "Disabled"}
            </p>

            <p>
              <strong>
                Phone Column:
              </strong>{" "}
              {phoneColumn || "N/A"}
            </p>

            <p>
              <strong>
                Template:
              </strong>{" "}
              {selectedTemplate || "N/A"}
            </p>

            <p>
              <strong>
                AI Followup:
              </strong>{" "}
              {aiFollowupEnabled
                ? "Enabled"
                : "Disabled"}
            </p>

            <p>
              <strong>
                Followup Delay:
              </strong>{" "}
              {followupDelay} minutes
            </p>

            <button
              className="button"
              style={{
                marginTop:
                  "20px"
              }}
              onClick={
                handleStartCampaign
              }
            >
              Start Campaign
            </button>

          </div>
        )}

        {/* ========================================= */}
        {/* 🔥 DASHBOARD */}
        {/* ========================================= */}
        {activeTab ===
          "dashboard" && (

          <div className="card">

            <div className="section-title">
              Campaign Dashboard
            </div>

            <div className="dashboard-grid">

              {Array.isArray(
                campaigns
              ) &&
                campaigns.map(
                  (
                    campaign
                  ) => (

                    <div
                      className="dashboard-card"
                      key={
                        campaign.id
                      }
                    >

                      <h3>
                        {
                          campaign.name
                        }
                      </h3>

                      <p>
                        <strong>
                          Status:
                        </strong>{" "}
                        {
                          campaign.status
                        }
                      </p>

                      <p>
                        <strong>
                          Source:
                        </strong>{" "}
                        {
                          campaign.source_type
                        }
                      </p>

                      <p>
                        <strong>
                          Destination:
                        </strong>{" "}
                        {
                          campaign.destination_type
                        }
                      </p>

                      <p>
                        <strong>
                          Interval:
                        </strong>{" "}
                        {
                          campaign.interval_seconds
                        }s
                      </p>

                      <p>
                        <strong>
                          WhatsApp:
                        </strong>{" "}
                        {campaign.whatsapp_enabled
                          ? "Enabled"
                          : "Disabled"}
                      </p>

                      <p>
                        <strong>
                          Template:
                        </strong>{" "}
                        {
                          campaign.template_name ||
                          "N/A"
                        }
                      </p>

                      <p>
                        <strong>
                          AI Followup:
                        </strong>{" "}
                        {campaign.ai_followup_enabled
                          ? "Enabled"
                          : "Disabled"}
                      </p>

                      <p>
                        <strong>
                          Last Status:
                        </strong>{" "}
                        {
                          campaign.last_status ||
                          "Waiting"
                        }
                      </p>

                      {/* ACTIONS */}
                      <div
                        style={{
                          display:
                            "flex",

                          gap: "10px",

                          marginTop:
                            "15px",

                          flexWrap:
                            "wrap",
                        }}
                      >

                        {/* EDIT */}
                        <button
                          className="button"
                          onClick={() => {

                            setEditingCampaign(
                              campaign
                            );

                            setCampaignName(
                              campaign.name
                            );

                            setSyncInterval(
                              campaign.interval_seconds
                            );

                            setWhatsappEnabled(
                              campaign.whatsapp_enabled
                            );

                            setPhoneColumn(
                              campaign.phone_column || ""
                            );

                            setSelectedTemplate(
                              campaign.template_name || ""
                            );

                            setAiFollowupEnabled(
                              campaign.ai_followup_enabled
                            );

                            setFollowupDelay(
                              campaign.followup_delay_minutes || 30
                            );

                            setSavedConfig({

                              source: {

                                source_type:
                                  campaign.source_type,

                                file_id:
                                  campaign.source_file_id,

                                sheets:
                                  campaign.source_sheets,

                                gsheet_url:
                                  campaign.source_gsheet_url,

                                gsheet_name:
                                  campaign.source_gsheet_name,
                              },

                              destination: {

                                destination_type:
                                  campaign.destination_type,

                                file_id:
                                  campaign.destination_file_id,

                                sheet:
                                  campaign.destination_sheet,

                                gsheet_url:
                                  campaign.destination_gsheet_url,

                                gsheet_name:
                                  campaign.destination_gsheet_name,
                              },
                            });

                            setActiveTab(
                              "config"
                            );
                          }}
                        >
                          Edit
                        </button>

                        {/* START */}
                        <button
                          className="button"
                          onClick={async () => {

                            await fetch(
                              `${API_BASE_URL}/campaign/${campaign.id}/start`,
                              {
                                method:
                                  "POST",
                              }
                            );

                            loadCampaigns();
                          }}
                        >
                          Start
                        </button>

                        {/* PAUSE */}
                        <button
                          className="button"
                          onClick={async () => {

                            await fetch(
                              `${API_BASE_URL}/campaign/${campaign.id}/pause`,
                              {
                                method:
                                  "POST",
                              }
                            );

                            loadCampaigns();
                          }}
                        >
                          Pause
                        </button>

                        {/* DELETE */}
                        <button
                          className="button"
                          onClick={async () => {

                            const confirmDelete =
                              window.confirm(
                                "Delete this campaign?"
                              );

                            if (
                              !confirmDelete
                            )
                              return;

                            await fetch(
                              `${API_BASE_URL}/campaign/${campaign.id}`,
                              {
                                method:
                                  "DELETE",
                              }
                            );

                            loadCampaigns();
                          }}
                        >
                          Delete
                        </button>

                      </div>

                    </div>
                  )
                )}

            </div>

          </div>
        )}

      </div>

    </div>
  );
}

export default App;